import { timezoneToCountryCode } from "./timezoneToCountry";

export type TimezoneInfo = {
  value: string;
  label: string;
  offset: string;
};

function normalizeTimezoneValue(value: string): string {
  return value.replace(/_/g, " ").toLowerCase();
}

function getCanonicalTimezone(value: string): string {
  return value;
}

function getTimezoneOffsetString(timeZone: string): string {
  const now = new Date();
  const safeFormat = (
    zone: string,
    mode: "shortOffset" | "short"
  ): string | null => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: mode,
        hour12: false,
      }).format(now);
    } catch {
      return null;
    }
  };

  const withShortOffset = safeFormat(timeZone, "shortOffset");
  if (withShortOffset) {
    const gmtMatch = withShortOffset.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/i);
    if (gmtMatch) {
      const sign = gmtMatch[1].startsWith("-") ? "-" : "+";
      const hours = Math.abs(parseInt(gmtMatch[1], 10))
        .toString()
        .padStart(2, "0");
      const minutes = (gmtMatch[2] ? parseInt(gmtMatch[2], 10) : 0)
        .toString()
        .padStart(2, "0");
      return `UTC${sign}${hours}:${minutes}`;
    }
  }

  const withShort = safeFormat(timeZone, "short");
  if (withShort) {
    const gmtMatch2 = withShort.match(/GMT([+-]\d{1,2})/i);
    if (gmtMatch2) {
      const sign = gmtMatch2[1].startsWith("-") ? "-" : "+";
      const hours = Math.abs(parseInt(gmtMatch2[1], 10))
        .toString()
        .padStart(2, "0");
      return `UTC${sign}${hours}:00`;
    }
  }
  if (timeZone === "UTC") return "UTC+00:00";
  return "UTC";
}

function toFriendlyLabel(timeZone: string): string {
  if (timeZone === "UTC") return "UTC";
  const parts = timeZone.split("/");
  const city = parts[parts.length - 1].replace(/_/g, " ");
  return city;
}

export function detectUserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || "UTC";
  } catch {
    return "UTC";
  }
}

export function getTimezoneInfo(timeZone: string): TimezoneInfo {
  const canonical = getCanonicalTimezone(timeZone);
  const label = toFriendlyLabel(canonical);
  const offset = getTimezoneOffsetString(canonical);
  return { value: canonical, label, offset };
}

export function getCommonTimezones(): TimezoneInfo[] {
  const supported: string[] = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") || [
    "UTC",
    "America/Argentina/Buenos_Aires",
    "America/Mexico_City",
    "America/New_York",
    "America/Los_Angeles",
    "Europe/Madrid",
    "Europe/London",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];
  const unique = Array.from(new Set(supported));
  const infos = unique.map(getTimezoneInfo);
  const offsetToMinutes = (o: string) => {
    const m = o.match(/UTC([+-])(\d{2}):(\d{2})/);
    if (!m) return 0;
    const sign = m[1] === "-" ? -1 : 1;
    const h = parseInt(m[2], 10);
    const mm = parseInt(m[3], 10);
    return sign * (h * 60 + mm);
  };
  return infos
    .slice()
    .sort((a, b) => {
      const diff = offsetToMinutes(a.offset) - offsetToMinutes(b.offset);
      if (diff !== 0) return diff;
      return a.label.localeCompare(b.label);
    });
}

export function findTimezoneInList(detectedTimezone: string, list: TimezoneInfo[]): string {
  if (!detectedTimezone) return list[0]?.value || "UTC";
  const canonical = getCanonicalTimezone(detectedTimezone);
  const exact = list.find((t) => t.value === canonical);
  if (exact) return exact.value;
  const normalizedDetected = normalizeTimezoneValue(canonical);
  const partial = list.find((t) => normalizeTimezoneValue(t.value) === normalizedDetected);
  if (partial) return partial.value;
  const byCity = list.find((t) => normalizeTimezoneValue(t.label) === normalizedDetected || normalizeTimezoneValue(t.value.split("/").pop() || "") === normalizedDetected);
  if (byCity) return byCity.value;
  return list[0]?.value || "UTC";
}

export function formatDateTimeInTimezone(dateISO: string, time24: string, timeZone: string): string {
  const base = new Date(`${dateISO}T${time24}:00Z`);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const local = formatter.format(base);
  const offset = getTimezoneOffsetString(timeZone);
  return `${local} (${offset})`;
}

import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

export const BA_TIMEZONE = "America/Argentina/Buenos_Aires";

/**
 * Colapsa cualquier zona horaria argentina a Buenos Aires.
 *
 * Argentina tiene un único huso horario en todo el país (UTC-03:00, sin horario de verano
 * desde 2009), pero IANA define 12 zonas históricas — Salta, Cordoba, Mendoza, Ushuaia,
 * Rio_Gallegos, etc. — que hoy representan exactamente la misma hora. Mostrar (o guardar)
 * ciudades distintas para el mismo huso confunde sin aportar nada, así que se unifican.
 *
 * Se apoya en `timezoneToCountryCode`, que ya cubre las zonas canónicas, los aliases legacy
 * sin el segmento `/Argentina/` y el fallback por prefijo. Cualquier otro país queda intacto.
 *
 * @example normalizeTimezone("America/Argentina/Salta") // "America/Argentina/Buenos_Aires"
 * @example normalizeTimezone("America/Cordoba")         // "America/Argentina/Buenos_Aires"
 * @example normalizeTimezone("Europe/Madrid")           // "Europe/Madrid"
 */
export function normalizeTimezone(timeZone?: string | null): string {
  if (!timeZone) return "";
  return timezoneToCountryCode(timeZone) === "AR" ? BA_TIMEZONE : timeZone;
}

/**
 * ¿Dos zonas horarias marcan la misma hora durante todo el año?
 *
 * No alcanza con comparar el offset de hoy: Buenos Aires (-03:00 fijo) y Santiago de Chile
 * coinciden parte del año y se separan cuando entra el horario de verano chileno. Se comparan
 * enero y julio, que cubren el verano de cada hemisferio, así dos zonas se consideran
 * equivalentes solo si nunca difieren — sin importar que sean de países distintos.
 *
 * @example haveSameWallClockAllYear("America/Argentina/Buenos_Aires", "America/Montevideo") // true
 * @example haveSameWallClockAllYear("America/Argentina/Buenos_Aires", "America/Santiago")   // false
 */
export function haveSameWallClockAllYear(a: string, b: string): boolean {
  const year = new Date().getFullYear();
  return [Date.UTC(year, 0, 15), Date.UTC(year, 6, 15)].every((timestamp) => {
    const date = new Date(timestamp);
    return formatInTimeZone(date, a, "XXX") === formatInTimeZone(date, b, "XXX");
  });
}

export function formatDateISOInTimezone(date: Date, timeZone: string): string {
  return formatInTimeZone(date, timeZone, "yyyy-MM-dd");
}

export function isTodayInTimezone(date: Date, timeZone: string): boolean {
  const todayISO = formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd");
  const dateISO = formatInTimeZone(date, timeZone, "yyyy-MM-dd");
  return dateISO === todayISO;
}

export function isPastDateInTimezone(date: Date, timeZone: string): boolean {
  const todayISO = formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd");
  const dateISO = formatInTimeZone(date, timeZone, "yyyy-MM-dd");
  return dateISO < todayISO;
}

export function hasMinimumLeadTimeInTimezone(dateISO: string, time24: string, timeZone: string, minHours: number): boolean {
  const zonedDateTime = `${dateISO}T${time24}:00`;
  const candidateUtc = fromZonedTime(zonedDateTime, timeZone);
  const now = new Date();
  const minTime = new Date(now.getTime() + minHours * 60 * 60 * 1000);
  return candidateUtc.getTime() >= minTime.getTime();
}

/**
 * Parses a raw session datetime as received from the backend into the real
 * UTC instant it represents.
 *
 * Different endpoints return this in genuinely different shapes:
 * - "2026-07-28 19:00:00" (most endpoints) — naive, always Buenos Aires wall-clock
 *   time, NOT the browser's local timezone.
 * - "28-07-2026 19:00:00" (`/sessions/by-day`) — same, but day-first.
 * - "Tue Jul 28 2026 22:00:00 GMT+0000 (Coordinated Universal Time)"
 *   (`/sessions/by-day` in some responses) — a full `Date.prototype.toString()`
 *   dump, which already encodes its own absolute instant.
 * - ISO with an explicit offset/`Z` — also already absolute.
 *
 * Only the first two are naive and need the Buenos Aires wall-clock conversion;
 * everything else must be parsed as-is via `new Date(...)`, which understands
 * its own offset/timezone abbreviation. Reinterpreting an already-absolute
 * string as naive BA time (or misreading its date-order) silently produces an
 * Invalid Date or a instant shifted by hours — the source of both the
 * payment-countdown timezone drift and the "Invalid time value" crash.
 */
export function parseBackendSessionDate(raw: string, timeZone: string = BA_TIMEZONE): Date {
  const trimmed = raw.trim();
  // Only a bare "YYYY-MM-DD HH:MM[:SS]" or day-first "DD-MM-YYYY HH:MM[:SS]" is
  // naive local time with no offset info of its own. Anything else (ISO with
  // Z/offset, `Date.toString()` dumps, etc.) already parses correctly on its own.
  const naiveMatch = trimmed.match(/^(\d{2,4})-(\d{2})-(\d{2,4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!naiveMatch) return new Date(trimmed);
  const [, p1, p2, p3, hh, mm, ss] = naiveMatch;
  const [year, month, day] = p1.length === 4 ? [p1, p2, p3] : [p3, p2, p1];
  return fromZonedTime(`${year}-${month}-${day}T${hh}:${mm}:${ss || '00'}`, timeZone);
}

/**
 * Formatea un timezone IANA para mostrarlo como "Continente/Ciudad", quitando el
 * segmento intermedio de país cuando existe y reemplazando guiones bajos por espacios.
 *
 * Las zonas argentinas se unifican antes de formatear (ver `normalizeTimezone`), así que
 * cualquier ciudad del país se muestra como Buenos Aires.
 *
 * @example formatTimezoneName("America/Argentina/Salta")       // "America/Buenos Aires"
 * @example formatTimezoneName("America/Argentina/Buenos_Aires") // "America/Buenos Aires"
 * @example formatTimezoneName("Europe/Madrid")                  // "Europe/Madrid"
 */
export function formatTimezoneName(timeZone?: string | null): string {
  if (!timeZone || timeZone === "UTC") return timeZone ?? "";
  const normalized = normalizeTimezone(timeZone);
  const parts = normalized.split("/");
  if (parts.length <= 1) return normalized.replace(/_/g, " ");
  const continent = parts[0];
  const city = parts[parts.length - 1].replace(/_/g, " ");
  return `${continent}/${city}`;
}